import { useEffect, useState } from "react";
import { Download, X, Share, Plus, Smartphone } from "lucide-react";

const STORAGE_KEY = "in21_pwa_prompt_state";
const SHOW_AFTER_MS = 8000; // Show 8s after page load so it doesn't feel spammy
const DISMISS_COOLDOWN_DAYS = 5;

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent || "") && !window.MSStream;
}

function isSafari() {
  const ua = navigator.userAgent || "";
  return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
}

function getState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
}

function saveState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
}

function isRecentlyDismissed() {
  const s = getState();
  if (!s.dismissedAt) return false;
  const days = (Date.now() - s.dismissedAt) / (1000 * 60 * 60 * 24);
  return days < DISMISS_COOLDOWN_DAYS;
}

export default function PWAInstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [showIOSSheet, setShowIOSSheet] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Hide if already installed
    if (isStandalone()) return;
    // Hide if user dismissed recently
    if (isRecentlyDismissed()) return;
    // Hide on admin/reporter routes
    const path = window.location.pathname || "";
    if (path.startsWith("/admin") || path.startsWith("/reporter")) return;

    let showTimer;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      clearTimeout(showTimer);
      showTimer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      saveState({ installedAt: Date.now() });
    };
    window.addEventListener("appinstalled", onInstalled);

    // For iOS Safari (which doesn't fire beforeinstallprompt), still show after delay
    if (isIOS() && isSafari()) {
      showTimer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(showTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") saveState({ installedAt: Date.now() });
      else saveState({ dismissedAt: Date.now() });
      setDeferredPrompt(null);
      setVisible(false);
    } else if (isIOS()) {
      setShowIOSSheet(true);
    }
  };

  const dismiss = () => {
    saveState({ dismissedAt: Date.now() });
    setVisible(false);
    setShowIOSSheet(false);
  };

  if (!visible && !showIOSSheet) return null;

  // iOS instructions sheet
  if (showIOSSheet) {
    return (
      <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={dismiss} data-testid="pwa-ios-sheet">
        <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-br from-[#DC2626] via-[#EA580C] to-[#F59E0B] text-white px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <Smartphone className="w-6 h-6"/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-100">iPhone / iPad Install</div>
                <div className="font-devnagri-heading text-xl font-black leading-tight">INDIAN-NEWS-21 App</div>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm text-slate-700">बस 3 आसान step में यह पोर्टल आपके iPhone पर एक native app जैसा install हो जाएगा।</p>
            <div className="space-y-3">
              {[
                { n: 1, icon: <Share className="w-5 h-5"/>, text: "Safari के नीचे share button दबाओ" },
                { n: 2, icon: <Plus className="w-5 h-5"/>, text: '"Add to Home Screen" option select करो' },
                { n: 3, icon: <Download className="w-5 h-5"/>, text: 'Top-right पर "Add" tap करो — Done!' },
              ].map((s) => (
                <div key={s.n} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-[#DC2626] text-white flex items-center justify-center font-black text-sm">{s.n}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-slate-800 font-bold text-sm">{s.icon}<span>{s.text}</span></div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={dismiss} className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-black text-sm" data-testid="pwa-ios-close">समझ गया</button>
          </div>
        </div>
      </div>
    );
  }

  // Bottom install banner (Android/Chrome + iOS)
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[55] w-[calc(100%-1rem)] sm:max-w-md bottom-[76px] lg:bottom-4 animate-in slide-in-from-bottom duration-500"
      data-testid="pwa-install-banner"
    >
      <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]">
        {/* Colored accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#DC2626] via-[#EA580C] to-[#F59E0B]"/>
        <button onClick={dismiss} aria-label="बंद करो" className="absolute top-2 right-2 w-8 h-8 rounded-full hover:bg-white/10 text-slate-400 hover:text-white flex items-center justify-center transition" data-testid="pwa-dismiss">
          <X className="w-4 h-4"/>
        </button>
        <div className="p-4 pr-10 flex items-center gap-3">
          <div className="shrink-0 relative">
            <div className="w-14 h-14 rounded-xl bg-white shadow-md overflow-hidden ring-2 ring-amber-400/40 flex items-center justify-center">
              <img src="/logo.png" alt="INDIAN-NEWS-21" className="w-full h-full object-contain p-1" onError={(e) => (e.currentTarget.style.display = "none")}/>
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 ring-2 ring-[#0F172A] flex items-center justify-center">
              <Download className="w-3 h-3 text-white" strokeWidth={3}/>
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-black text-amber-300">Free App Install</div>
            <div className="font-devnagri-heading text-white font-black text-base leading-tight mt-0.5">INDIAN-NEWS-21 को app जैसा install करो</div>
            <div className="text-[11px] text-slate-400 mt-0.5">Home screen पर shortcut · offline पढ़ें · तुरंत breaking alerts</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 px-4 pb-4">
          <button onClick={dismiss} className="py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm font-bold border border-white/10 transition" data-testid="pwa-later">बाद में</button>
          <button onClick={handleInstall} className="py-2.5 rounded-lg bg-gradient-to-r from-[#DC2626] to-[#EA580C] hover:from-[#B91C1C] hover:to-[#DC2626] text-white text-sm font-black shadow-lg transition flex items-center justify-center gap-1.5" data-testid="pwa-install">
            <Download className="w-4 h-4"/>अभी Install करो
          </button>
        </div>
      </div>
    </div>
  );
}
